import React, { useCallback, useMemo } from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';

import { H3 } from '@r1-frontend/ui-react/components/typography/heading';

import { CustomSwiper } from '~/src/components/CustomSwiper';
import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import { MainLayout } from '~/src/features/full-buy/containers/MainLayout/MainLayout';
import {
    CombinedSubscriptionContainerForTariffItem,
} from '~/src/features/full-buy/containers/Subscriptions';
import { renderTariffList } from '~/src/features/full-buy/renderTariffList';
import { SelectedTariffElements } from '~/src/features/full-buy/SelectedTariffElements';
import { selectTariff } from '~/src/features/full-buy/store/actions';
import getEquipment from '~/src/features/full-buy/store/actions/getEquipment';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import { selectFilteredTariffs } from '~/src/features/full-buy/store/selectors/selectFilteredTariffs';
import { IFullBuyTariff } from '~/src/features/full-buy/store/tariffTransformer';
import { useFiltersByTags } from '~/src/features/full-buy/useFiltersByTags';
import { TState } from '~/src/store';

import * as S from '~/src/features/full-buy/containers/pages/styled';
import * as ST from './styled';

const hasBundleTariff = (tariffs: IFullBuyTariff[]) => tariffs.some(el => el.isMono === false);

export const TariffInfo = (): JSX.Element => {
    const analytics = useAnalytics();
    const dispatch = useDispatch();

    const {
        selectedTariff,
        filteredTariffs,
        displayTariffLine,
    } = useSelector((state: TState) => ({
        selectedTariff: selectCurrentTariff(state),
        filteredTariffs: selectFilteredTariffs(state),
        displayTariffLine: state.fullBuy._displayTariffLine,
    }), shallowEqual);

    const tariffName = selectedTariff?.title || '';

    const onSelectTariff = useCallback((_tariff: IFullBuyTariff) => {
        batch(() => {
            dispatch(selectTariff({
                salePackageId: _tariff.sale_package_id,
            }));
            dispatch(getEquipment());
        });

    }, [dispatch]);

    const defaultDisplayMono = !!selectedTariff?.isMono || !hasBundleTariff(filteredTariffs);
    const { TagFilters, tariffsFilterByTags } = useFiltersByTags(filteredTariffs, defaultDisplayMono);

    const slideIndex = tariffsFilterByTags.findIndex((el) => el.sale_package_id === selectedTariff?.sale_package_id);
    const swiperKey = useMemo(() => {
        return tariffsFilterByTags.map(tariff => tariff.sale_package_id).join(',');
    }, [tariffsFilterByTags]);

    return (
        <MainLayout>
            <H3 $withIndent={false}>{tariffName ? `Вы выбрали тариф «${tariffName}»` : 'Выберите тариф'}</H3>
            <S.BlocksWrapper>
                {!!filteredTariffs.length && displayTariffLine &&
                    <ST.TariffLineWrapperFullWidth>
                        <TagFilters/>

                        <CustomSwiper
                            slideIndex={slideIndex}
                            key={swiperKey}
                        >
                            {renderTariffList({
                                tariffPackages: tariffsFilterByTags,
                                selectedPackageId: selectedTariff?.sale_package_id,
                                withArrowPointer: true,
                                onSelectTariff,
                                sendAnalytics: (name: string) => analytics.tariff.chooseRecommended(name),
                            })}
                        </CustomSwiper>
                    </ST.TariffLineWrapperFullWidth>
                }

                {selectedTariff &&
                    <SelectedTariffElements
                        tariffPackage={selectedTariff}
                    />
                }
            </S.BlocksWrapper>

            <CombinedSubscriptionContainerForTariffItem />
        </MainLayout>
    );
};
